import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Lock, ShieldCheck, Users, Wallet, Briefcase, Eye } from 'lucide-react';
import { useAuth, type Role } from '../../lib/auth';
import { Alert } from '../../components/ui';

const roleHome: Record<Role, string> = {
  admin: '/admin',
  oversight: '/it',
  manager: '/manager',
  staff: '/staff',
  account: '/accounts',
};

const PORTALS: Record<Role, { label: string; heading: string; icon: React.ReactNode; description: string }> = {
  admin: {
    label: 'Admin',
    heading: 'Admin Portal Login',
    icon: <ShieldCheck className="h-12 w-12 text-primary" />,
    description: 'Full system control',
  },
  oversight: {
    label: 'Oversight',
    heading: 'Oversight Portal Login',
    icon: <Eye className="h-12 w-12 text-primary" />,
    description: 'Monitoring & user management',
  },
  manager: {
    label: 'Manager',
    heading: 'Manager Portal Login',
    icon: <Briefcase className="h-12 w-12 text-primary" />,
    description: 'Team management',
  },
  staff: {
    label: 'Staff',
    heading: 'Staff Portal Login',
    icon: <Users className="h-12 w-12 text-primary" />,
    description: 'Daily operations',
  },
  account: {
    label: 'Accounts',
    heading: 'Accounts Portal Login',
    icon: <Wallet className="h-12 w-12 text-primary" />,
    description: 'Finance & expenses',
  },
};

export default function PortalLogin({ role }: { role: Role }) {
  const navigate = useNavigate();
  const { login, logout } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const portal = PORTALS[role];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const user = await login(email, password);
      if (user.role !== role) {
        logout();
        setError(`This account belongs to the ${PORTALS[user.role].label} portal and cannot sign in from the ${portal.label} portal.`);
        return;
      }
      navigate(roleHome[user.role]);
    } catch (err) {
      if (err instanceof Error && err.message && !/failed to fetch/i.test(err.message)) {
        setError(err.message);
      } else {
        setError('Unable to reach the server. Please check your connection and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ivory flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          {portal.icon}
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold text-obsidian">{portal.heading}</h2>
        <p className="mt-2 text-center text-sm text-taupe">
          Sign in to the {portal.label} portal of Naya Edwin Solutions
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && <Alert>{error}</Alert>}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-charcoal">
                Email address
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-charcoal">
                Password
              </label>
              <div className="mt-1 relative">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <Lock className="h-5 w-5 text-taupe" />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <Link to={`/forgot-password?portal=${role}`} className="text-sm text-primary hover:text-primary-dark">
                Forgot password?
              </Link>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-black bg-primary hover:bg-primary-light focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
              >
                {loading ? 'Signing in...' : `Sign in to ${portal.label} Portal`}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
